import { useCallback } from "react";
import {
  getPDA1050_R01ItemOne,
  getPDA1050_R01List,
  getPDA1050_R01One,
  postPDA1050_R01,
} from "src/apis/PDA1050_R01Api";
import { useToast } from "src/hooks/useToast";
import usePDA1050_R01Store from "./Store_PDA1050_R01";
import {
  PDA1050_R01Filter,
  PDA1050_R01HeaderDto,
  PDA1050_R01TransportDto,
} from "./PDA1050_R01Dto";

/**
 * Axios 오류 응답에 서버 메시지가 있으면 이를 반환하고, 없으면 화면별 기본 메시지를 반환한다.
 * 오류 객체의 형태를 알 수 없는 경우에도 화면에서 안전하게 문자열을 표시하기 위한 보조 함수다.
 *
 * @param error API 호출 중 전달된 오류 객체
 * @param fallback 서버 메시지를 찾지 못했을 때 사용할 기본 메시지
 * @returns 사용자에게 표시할 오류 메시지
 */
const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseError = error as { response?: { data?: { message?: string } } };
  return responseError.response?.data?.message || fallback;
};

/**
 * PDA1050_R01 화면의 조회, 발주 선택, 바코드 검증, 검수 및 입고등록 흐름을 제공한다.
 * 각 처리 함수는 API 호출과 Zustand 상태 갱신을 함께 담당하며, 성공/경고/오류 메시지를 화면에 표시한다.
 *
 * @returns 화면 컴포넌트에서 사용하는 업무 처리 함수 모음
 */
export const usePDA1050_R01Content = () => {
  const store = usePDA1050_R01Store();
  const { success, warning, error } = useToast();

  /**
   * 입력받은 검색 조건 또는 Store의 현재 검색 조건으로 발주 목록을 조회한다.
   * 조회 전후로 로딩 상태를 변경하고, 결과가 없거나 요청에 실패하면 기존 목록을 비운다.
   *
   * @param condition 바코드 스캔 등으로 즉시 적용할 검색 조건. 생략하면 Store 조건을 사용한다.
   */
  const handleSearchOrders = useCallback(
    async (condition?: PDA1050_R01Filter) => {
      const searchCondition = condition ?? store.PDA1050_R01Filter;

      store.setIsLoading(true);
      try {
        const response = await getPDA1050_R01List(searchCondition);
        const orderList = response.data.data || [];
        if (!response.data.successFlag || orderList.length === 0) {
          store.setPDA1050_R01List([]);
          warning("조회된 발주가 없습니다.");
          return;
        }

        store.setPDA1050_R01List(orderList);
        success(`${orderList.length}건의 발주를 조회했습니다.`);
      } catch (requestError: unknown) {
        store.setPDA1050_R01List([]);
        error(
          getErrorMessage(requestError, "발주 목록 조회 중 오류가 발생했습니다.")
        );
      } finally {
        store.setIsLoading(false);
      }
    },
    [store, success, warning, error]
  );

  /**
   * 목록에서 선택한 발주의 Header/Detail을 조회하고 입고등록 화면의 초기 상태를 구성한다.
   * 이전 입고 이력이 있는 품목이 있으면 해당 품목의 최근 창고를 기본 창고로 복원한다.
   * 조회 실패 시 선택 정보, Detail, 검수 목록, 스캔값 및 창고를 모두 초기화한다.
   *
   * @param order 목록에서 선택한 발주 Header
   * @returns 상세 화면으로 전환할 수 있으면 true, 조회에 실패하면 false
   */
  const handleSelectOrder = useCallback(async (order: PDA1050_R01HeaderDto) => {
    const purchaseOrderNo = order.purchaseOrderNo.trim();

    store.setIsLoading(true);
    try {
      const response = await getPDA1050_R01One(purchaseOrderNo);
      if (!response.data.successFlag || !response.data.data) {
        throw new Error("발주 조회 실패");
      }

      const { headerDto, detailDtoList } = response.data.data;
      store.setPurchaseOrderNo(headerDto.purchaseOrderNo);
      store.setSelectedPDA1050_R01(headerDto);
      store.setPDA1050_R01DetailList(detailDtoList || []);
      store.setCheckedPDA1050_R01List([]);
      store.initScan();
      store.setWarehouseCd("");
      store.setWarehouseNm("");

      const previousWarehouse = (detailDtoList || []).find(
        (detail) => detail.warehouseCd
      );
      if (previousWarehouse?.warehouseCd) {
        store.setWarehouseCd(previousWarehouse.warehouseCd);
        store.setWarehouseNm(previousWarehouse.warehouseNm || "");
      }
      return true;
    } catch (requestError: unknown) {
      store.setSelectedPDA1050_R01(null);
      store.setPDA1050_R01DetailList([]);
      store.setCheckedPDA1050_R01List([]);
      store.initScan();
      store.setWarehouseCd("");
      store.setWarehouseNm("");
      error(getErrorMessage(requestError, "발주 조회 중 오류가 발생했습니다."));
      return false;
    } finally {
      store.setIsLoading(false);
    }
  }, [store, error]);

  /**
   * 스캔값의 공백을 제거한 뒤 현재 발주에 포함된 품목인지 서버에서 검증한다.
   * 성공하면 스캔 품목을 Store에 저장하고, 실패하면 기존 스캔 품목을 제거한다.
   *
   * @param scannedBarcode 스캐너 또는 키보드로 입력된 바코드/품목코드
   * @returns 스캔 품목 조회 및 검증 성공 여부
   */
  const handleScanBarcode = useCallback(
    async (scannedBarcode: string) => {
      if (!store.selectedPDA1050_R01) {
        warning("발주를 먼저 조회하세요.");
        return false;
      }
      const barcode = scannedBarcode.trim();
      if (!barcode) {
        warning("바코드를 입력하세요.");
        return false;
      }

      store.setBarcode(barcode);
      store.setIsLoading(true);
      try {
        const response = await getPDA1050_R01ItemOne(
          store.selectedPDA1050_R01.purchaseOrderNo,
          barcode
        );
        if (!response.data.successFlag || !response.data.data) {
          throw new Error("품목 조회 실패");
        }
        store.setScannedItem(response.data.data);
        return true;
      } catch (requestError: unknown) {
        store.setScannedItem(null);
        error(getErrorMessage(requestError, "품목 조회 중 오류가 발생했습니다."));
        return false;
      } finally {
        store.setIsLoading(false);
      }
    },
    [store, warning, error]
  );

  /**
   * 현재 스캔 품목과 입력 수량을 검수 목록에 반영한다.
   * 발주 포함 여부, 창고, 양수 수량, 잔여 입고 가능수량을 확인한 후 동일 품목이면 수량을 누적한다.
   * 정상 반영 후 다음 스캔을 받을 수 있도록 스캔 품목, 바코드 및 수량을 초기화한다.
   *
   * @returns 검수 목록에 반영되면 true, 검증에 실패하면 false
   */
  const handleCheck = useCallback(() => {
    const item = store.scannedItem;
    if (!item) {
      warning("바코드를 먼저 스캔하세요.");
      return false;
    }
    if (!store.warehouseCd) {
      warning("입고 창고를 선택하세요.");
      return false;
    }
    if (store.transportQty <= 0) {
      warning("입고량은 0보다 커야 합니다.");
      return false;
    }

    const detail = store.PDA1050_R01DetailList.find(
      (row) => row.itemCd === item.itemCd
    );
    if (!detail) {
      warning("해당 품목은 발주에 포함되어 있지 않습니다.");
      return false;
    }

    const checkedQty = store.checkedPDA1050_R01List.find(
      (row) => row.itemCd === item.itemCd
    )?.qty || 0;
    if (checkedQty + store.transportQty > detail.remainQty) {
      warning(
        `입고 가능수량을 초과했습니다. 가능 ${detail.remainQty.toLocaleString()}`
      );
      return false;
    }

    const exists = store.checkedPDA1050_R01List.some(
      (row) => row.itemCd === item.itemCd
    );
    const nextList = exists
      ? store.checkedPDA1050_R01List.map((row) =>
          row.itemCd === item.itemCd
            ? { ...row, qty: row.qty + store.transportQty }
            : row
        )
      : [
          ...store.checkedPDA1050_R01List,
          {
            itemCd: item.itemCd,
            itemNm: item.itemNm,
            barcode: store.barcode,
            qty: store.transportQty,
          },
        ];

    store.setCheckedPDA1050_R01List(nextList);
    store.initScan();
    return true;
  }, [store, warning]);

  /**
   * 검수 목록을 PDA1050 입고등록 DTO로 변환하여 서버에 저장한다.
   * 저장 성공 후 검수/스캔 상태를 초기화하고 동일 발주를 재조회하여 누적 입고량과 잔량을 갱신한다.
   * 필수 값이 없거나 저장에 실패하면 상태를 보존하고 사용자에게 원인을 표시한다.
   */
  const handleComplete = useCallback(async () => {
    const header = store.selectedPDA1050_R01;
    if (!header) {
      warning("발주를 먼저 조회하세요.");
      return;
    }
    if (!store.warehouseCd) {
      warning("입고 창고를 선택하세요.");
      return;
    }
    if (store.checkedPDA1050_R01List.length === 0) {
      warning("검수된 품목이 없습니다.");
      return;
    }

    const request: PDA1050_R01TransportDto = {
      valueIndex: 0,
      orderIndex: 0,
      orderIndexSeq: 0,
      transportNo: "",
      purchaseOrderNo: header.purchaseOrderNo,
      compCd: "",
      logicCenter: header.logicCenter || "",
      warehouseCd: store.warehouseCd,
      transportDt: store.transportDt,
      inoutType: "110",
      inoutClass: "110",
      customCd: header.customCd,
      otherLogicCenter: "",
      inoutMethod: "",
      empNo: "",
      transAmt: 0,
      vatAmt: 0,
      totalAmt: 0,
      telNo: "",
      faxNo: "",
      email: "",
      post: "",
      address: "",
      otherAddress: "",
      trackingNo: "",
      wmsOrderNo: "",
      approveFlg: "Y",
      remark: "",
      inserted: true,
      isModified: false,
      detailDtoList: store.checkedPDA1050_R01List.map((item, index) => ({
        valueIndex: index,
        orderIndex: index,
        orderIndexSeq: index,
        transportNo: "",
        itemNo: "",
        compCd: "",
        logicCenter: header.logicCenter || "",
        transportDt: store.transportDt,
        inoutType: "110",
        itemCd: item.itemCd,
        itemStatus: "A",
        unit: "",
        qty: item.qty,
        cost: 0,
        amt: 0,
        remark: "",
        inserted: true,
        isModified: false,
        barcode: item.barcode,
      })),
    };

    store.setIsLoading(true);
    try {
      const response = await postPDA1050_R01(request);
      if (!response.data.successFlag) {
        throw new Error(response.data.message || "입고 저장 실패");
      }
      success("입고등록이 완료되었습니다.");
      store.setCheckedPDA1050_R01List([]);
      store.initScan();

      const reloadResponse = await getPDA1050_R01One(header.purchaseOrderNo);
      if (reloadResponse.data.successFlag && reloadResponse.data.data) {
        store.setSelectedPDA1050_R01(reloadResponse.data.data.headerDto);
        store.setPDA1050_R01DetailList(
          reloadResponse.data.data.detailDtoList || []
        );
      }
    } catch (requestError: unknown) {
      error(getErrorMessage(requestError, "입고 저장 중 오류가 발생했습니다."));
    } finally {
      store.setIsLoading(false);
    }
  }, [store, warning, success, error]);

  return {
    handleSearchOrders,
    handleSelectOrder,
    handleScanBarcode,
    handleCheck,
    handleComplete,
  };
};
